import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import EngineerLayout from './EngineerLayout';
import { db } from '../../firebase';
import { ref, onValue } from 'firebase/database';
import { 
    FaArrowLeft, 
    FaPrint,
    FaChevronRight,
    FaSort
} from 'react-icons/fa';

const EngineerSocietyInterestedWorksDetail = () => {
    const navigate = useNavigate();
    const { id } = useParams();

    const [applicants, setApplicants] = useState([]);
    const [workDetails, setWorkDetails] = useState(null);
    const [loading, setLoading] = useState(true);

    // Marathi numerals for Sr.No.
    const marathiNumerals = ['०', '१', '२', '३', '४', '५', '६', '७', '८', '९'];
    const toMarathiNumber = (n) => String(n).split('').map(digit => marathiNumerals[parseInt(digit)] || digit).join('');

    useEffect(() => {
        setLoading(true);
        if (!id) return;

        let engData = {}, socData = {};
        let engDone = false, socDone = false;

        const processApplicants = (work) => {
            if (engDone && socDone) {
                const allRegs = [
                    ...Object.entries(engData).map(([key, val]) => ({ ...val, _key: key })),
                    ...Object.entries(socData).map(([key, val]) => ({ ...val, _key: key }))
                ];

                const interested = allRegs.filter(reg => {
                    if (reg.work_payment && reg.work_payment[work.meetingNumber]) {
                        const selected = reg.work_payment[work.meetingNumber].selectedWorks || [];
                        return selected.some(s => s.startsWith(`${work.workNumber} -`));
                    }
                    return false;
                }).map(reg => ({
                    id: reg._key,
                    loginId: reg.userName || reg.loginId || reg.registrationNo || "-",
                    institution: reg.societyName || reg.fullName || reg.name || "-",
                    phone: reg.chairmanWhatsapp || reg.mobile || reg.phoneNumber || "-",
                    workName: work.workName
                }));

                setApplicants(interested);
                setLoading(false);
            }
        };

        const fetchApplicants = (work) => {
            const engRef = ref(db, 'ekamwatapzp/EngineerRegistrations');
            const socRef = ref(db, 'ekamwatapzp/SocietyRegistrations');

            onValue(engRef, (snap) => { engData = snap.val() || {}; engDone = true; processApplicants(work); }, { onlyOnce: true });
            onValue(socRef, (snap) => { socData = snap.val() || {}; socDone = true; processApplicants(work); }, { onlyOnce: true });
        };

        // 1. Fetch Work Details
        const worksRef = ref(db, 'ekamwatapzp/WorkNames');
        onValue(worksRef, (snapshot) => {
            if (snapshot.exists()) {
                const data = snapshot.val();
                let foundWork = null;
                
                Object.entries(data).forEach(([key, val]) => {
                    if (foundWork) return;
                    if (val && typeof val === 'object') {
                        if (val.schemeName) {
                            if (key === id) foundWork = { id: key, meetingNumber: val.meetingNumber || "General", ...val };
                        } else {
                            Object.entries(val).forEach(([innerId, innerVal]) => {
                                if (innerId === id) foundWork = { id: innerId, meetingNumber: key, ...innerVal };
                            });
                        }
                    }
                });

                if (foundWork) {
                    setWorkDetails(foundWork);
                    fetchApplicants(foundWork);
                } else {
                    setLoading(false);
                }
            }
        }, { onlyOnce: true });

    }, [id]);

    return (
        <EngineerLayout>
            <div className="p-8 bg-[#f4f7fe] min-h-screen font-marathi">
                {/* Header Section */}
                <div className="flex justify-between items-center mb-10 px-2 text-left no-print">
                    <div className="flex items-center gap-6">
                        <button 
                            onClick={() => window.history.back()}
                            className="p-4 bg-white rounded-2xl shadow-sm hover:bg-gray-50 transition-all text-gray-600 border border-gray-100 hover:scale-105 active:scale-95 group"
                        >
                            <FaArrowLeft size={18} className="group-hover:-translate-x-1 transition-transform" />
                        </button>
                        <h1 className="text-2xl font-black text-gray-800 tracking-tight">सोसायटी इंटरेस्टेड अर्ज पहा</h1>
                    </div>
                </div>

                {/* Detail Card Content */}
                <div className="bg-white rounded-[2rem] shadow-sm border border-gray-100 p-10">
                    <div className="mb-6">
                        <h2 className="text-lg font-black text-blue-600 mb-1">{workDetails?.workName || "काम लोड होत आहे..."}</h2>
                        <p className="text-gray-400 text-sm font-bold tracking-widest uppercase">बैठक क्रमांक: {workDetails?.meetingNumber || "-"}</p>
                    </div>

                    <div className="overflow-hidden border border-gray-100 rounded-[1.5rem] shadow-sm">
                        {loading ? (
                            <div className="p-20 flex flex-col items-center justify-center space-y-4">
                                <div className="animate-spin text-blue-600 text-4xl">
                                    <FaSort />
                                </div>
                                <p className="text-gray-400 font-bold animate-pulse uppercase tracking-widest text-xs">माहिती लोड होत आहे...</p>
                            </div>
                        ) : (
                            <table className="w-full border-collapse">
                                <thead>
                                    <tr className="bg-white border-b border-gray-200 text-[13px] font-black text-gray-800 text-left uppercase">
                                        <th className="px-8 py-6 border-r border-gray-100 w-24">#</th>
                                        <th className="px-8 py-6 border-r border-gray-100">लॉगिन आयडी</th>
                                        <th className="px-8 py-6 border-r border-gray-100 min-w-[350px]">संस्थेचे नाव</th>
                                        <th className="px-8 py-6 border-r border-gray-100">फोन नंबर.</th>
                                        <th className="px-8 py-6 min-w-[350px]">कामाचे नाव</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {applicants.length > 0 ? applicants.map((item, index) => (
                                        <tr key={index} className="hover:bg-blue-50/20 group transition-colors border-b border-gray-100 last:border-0 text-[15px] font-bold text-gray-700">
                                            <td className="px-8 py-7 border-r border-gray-100 text-gray-400">{toMarathiNumber(index + 1)}</td>
                                            <td className="px-8 py-7 border-r border-gray-100">{item.loginId}</td>
                                            <td className="px-8 py-7 border-r border-gray-100 font-marathi leading-relaxed text-gray-800">{item.institution}</td>
                                            <td className="px-8 py-7 border-r border-gray-100">{item.phone}</td>
                                            <td className="px-8 py-7 font-marathi leading-relaxed text-gray-600 group-hover:text-blue-600 transition-colors uppercase">{item.workName}</td>
                                        </tr>
                                    )) : (
                                        <tr>
                                            <td colSpan="5" className="px-8 py-20 text-center text-gray-400 font-bold">
                                                या कामासाठी कोणतेही इंटरेस्टेड अर्ज आढळले नाहीत.
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        )}
                    </div>

                    {/* Print Button */}
                    <div className="mt-12 flex justify-center no-print">
                        <button 
                            onClick={() => window.print()}
                            className="bg-indigo-600 hover:bg-indigo-700 text-white px-12 py-3.5 rounded-xl font-bold shadow-lg shadow-indigo-500/30 transition transform active:scale-95 flex items-center gap-3 tracking-wider uppercase"
                        >
                            <FaPrint size={14} />
                            प्रिंट
                        </button>
                    </div>
                </div>

                {/* Optional Design Insight */}
                <div className="mt-12 text-center">
                    <p className="text-[10px] text-gray-400 font-bold uppercase tracking-[0.3em]">
                        Validated Society Interest Records • System Integrity Check
                    </p>
                </div>
            </div>

            <style>{`
                .font-marathi {
                    font-family: 'Hind', sans-serif;
                }
                @import url('https://fonts.googleapis.com/css2?family=Hind:wght@400;500;600;700&display=swap');
                @media print {
                    .no-print { display: none !important; }
                    body { background: white !important; margin: 0; padding: 0; }
                    .p-8 { padding: 0 !important; }
                    .bg-[#f4f7fe] { background: white !important; }
                    .bg-white { background: white !important; padding: 0 !important; border: none !important; box-shadow: none !important; }
                    .rounded-[2rem], .rounded-[1.5rem] { border-radius: 0 !important; border: none !important; box-shadow: none !important; }
                    table { width: 100% !important; border-collapse: collapse !important; margin: 0 !important; }
                    th, td { border: 1px solid #eee !important; padding: 12px 8px !important; text-align: left !important; }
                    th { background-color: #f8fafc !important; -webkit-print-color-adjust: exact; }
                    .mb-6 { margin-bottom: 20px !important; }
                    h2 { color: black !important; font-size: 18px !important; }
                }
            `}</style>
        </EngineerLayout>
    );
};

export default EngineerSocietyInterestedWorksDetail;
